import { useEffect, useState } from "react";
import { Search, Users, Calendar, Mail, ListChecks, Command } from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { TID } from "@/constants/testIds";

export function CommandPalette({ open, onClose }) {
    const [q, setQ] = useState("");
    const [res, setRes] = useState({ clients: [], webinars: [], newsletters: [], tasks: [], recurrences: [] });
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) setQ("");
    }, [open]);

    useEffect(() => {
        if (!q || q.length < 2) {
            setRes({ clients: [], webinars: [], newsletters: [], tasks: [], recurrences: [] });
            return;
        }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
                setRes(data);
            } catch {}
        }, 220);
        return () => clearTimeout(t);
    }, [q]);

    if (!open) return null;

    const nav = (path) => {
        onClose();
        navigate(path);
    };

    const total = res.clients.length + res.webinars.length + res.newsletters.length +
                  res.tasks.length + res.recurrences.length;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4" role="dialog">
            <div
                className="absolute inset-0 bg-navy/40 backdrop-blur-md"
                onClick={onClose}
            />
            <div className="relative w-full max-w-2xl rounded-2xl pv-glass shadow-2xl overflow-hidden border border-border/[var(--border-opacity)] pv-enter">
                <div className="flex items-center gap-3 px-5 py-4 border-b border-border/[var(--border-opacity)]">
                    <Search size={17} strokeWidth={1.5} className="text-textmuted" />
                    <input
                        autoFocus
                        data-testid={TID.searchInput}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca clienti, webinar, newsletter, task, ricorrenze..."
                        className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-textsubtle"
                    />
                    <kbd className="pv-mono text-[10px] px-1.5 py-0.5 rounded-md border border-border/[var(--border-opacity)] text-textsubtle">esc</kbd>
                </div>
                <div className="max-h-[54vh] overflow-y-auto pv-scroll py-2">
                    {q.length < 2 && (
                        <div className="px-6 py-10 text-center text-[13px] text-textsubtle">
                            Digita almeno 2 caratteri per iniziare la ricerca universale.
                            <div className="mt-4 flex flex-wrap justify-center gap-2 text-[11px]">
                                {["World Sight Day", "Recall", "Milano", "Reel"].map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setQ(s)}
                                        className="px-2 py-1 rounded-md border border-border/[var(--border-opacity)] hover:bg-surface2 transition-colors"
                                    >{s}</button>
                                ))}
                            </div>
                        </div>
                    )}
                    {q.length >= 2 && total === 0 && (
                        <div className="px-6 py-10 text-center text-[13px] text-textsubtle">Nessun risultato.</div>
                    )}
                    {res.clients.length > 0 && (
                        <Section title="Clienti" icon={Users}>
                            {res.clients.map((c) => (
                                <Row key={c.id} onClick={() => nav(`/crm/${c.id}`)}
                                     left={`${c.first_name} ${c.last_name}`}
                                     mid={c.studio} right={c.state} />
                            ))}
                        </Section>
                    )}
                    {res.recurrences.length > 0 && (
                        <Section title="Ricorrenze Sanitarie" icon={Calendar}>
                            {res.recurrences.map((r) => (
                                <Row key={r.id} onClick={() => nav("/health-calendar")}
                                     left={r.name_it} mid={r.name_en} right={r.category} />
                            ))}
                        </Section>
                    )}
                    {res.webinars.length > 0 && (
                        <Section title="Webinar" icon={Calendar}>
                            {res.webinars.map((w) => (
                                <Row key={w.id} onClick={() => nav("/marketing")}
                                     left={w.title} mid={w.date} right={`${w.attended} presenti`} />
                            ))}
                        </Section>
                    )}
                    {res.newsletters.length > 0 && (
                        <Section title="Newsletter" icon={Mail}>
                            {res.newsletters.map((n) => (
                                <Row key={n.id} onClick={() => nav("/newsletter")}
                                     left={n.subject} mid={n.motivation} right={n.status} />
                            ))}
                        </Section>
                    )}
                    {res.tasks.length > 0 && (
                        <Section title="Tasks" icon={ListChecks}>
                            {res.tasks.map((t) => (
                                <Row key={t.id} onClick={() => nav("/tasks")}
                                     left={t.title} mid={t.assignee} right={t.priority} />
                            ))}
                        </Section>
                    )}
                </div>
                <div className="border-t border-border/[var(--border-opacity)] px-4 py-2 flex items-center justify-between text-[11px] text-textsubtle">
                    <div className="flex items-center gap-1"><Command size={11} strokeWidth={1.5} /> K per aprire</div>
                    <div>enter per aprire · esc per chiudere</div>
                </div>
            </div>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <div className="mb-2">
            <div className="px-4 pt-2 pb-1 flex items-center gap-2 pv-eyebrow">
                <Icon size={11} strokeWidth={1.5} /> {title}
            </div>
            {children}
        </div>
    );
}

function Row({ left, mid, right, onClick }) {
    return (
        <button
            onClick={onClick}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primary/[0.06] transition-colors text-left"
        >
            <span className="text-[13.5px] font-medium flex-1 truncate">{left}</span>
            <span className="text-[12px] text-textmuted flex-1 truncate hidden md:block">{mid}</span>
            <span className="text-[11px] text-textsubtle pv-mono">{right}</span>
        </button>
    );
}
